import React from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Controller, Control } from 'react-hook-form'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import DepartmentSelectMutiple from '@/components/Form/Shared/DepartmentSelectMutiple'
import { Info } from 'lucide-react'

interface SharingSectionProps {
    control: Control<any>
    shareScope: 'public' | 'private'
    isRegulation?: boolean
}

const SharingSection: React.FC<SharingSectionProps> = ({
    control,
    shareScope,
    isRegulation = false,
}) => {
    const { t } = useTranslation('asset')

    return (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-indigo-900">
                    {t('form.sharing')}
                </h4>
            </div>

            {/* Show info message for REGULATION documents */}
            {isRegulation && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm text-blue-700">
                        {t('form.regulationAppliesCompanyWide')}
                    </span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="shareScope">{t('form.shareScope')}</Label>
                    <Controller
                        control={control}
                        name="shareScope"
                        render={({ field }) => (
                            <Select
                                value={isRegulation ? 'public' : field.value}
                                onValueChange={field.onChange}
                                disabled={isRegulation}
                            >
                                <SelectTrigger
                                    id="shareScope"
                                    disabled={isRegulation}
                                >
                                    <SelectValue
                                        placeholder={t('form.shareScope')}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="public">
                                        {t('form.sharePublic')}
                                    </SelectItem>
                                    <SelectItem value="private">
                                        {t('form.sharePrivate')}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                </div>
            </div>

            {shareScope === 'private' && !isRegulation && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <EmployeeSelect
                            control={control}
                            name="shared_employee_ids"
                            multiple
                            label={t('form.shareWithEmployees')}
                        />
                    </div>
                    <div>
                        <DepartmentSelectMutiple
                            control={control}
                            name="shared_department_ids"
                            multiple
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default SharingSection
